import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useCpdEnabled, useTenant } from "@/contexts/TenantContext";
import { LeaderboardFilters } from "@/components/leaderboards/LeaderboardFilters";
import { LeaderboardTable } from "@/components/leaderboards/LeaderboardTable";
import { UserStatsCard } from "@/components/leaderboards/UserStatsCard";
import {
  fetchLeaderboard,
  fetchUserRank,
  fetchAgentsForFilter,
  fetchCompaniesForFilter,
  FilterOptions,
} from "@/lib/api/leaderboard";
import { Trophy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function Leaderboards() {
  const { user, hasRole } = useAuth();
  const { tenant } = useTenant();
  const cpdEnabled = useCpdEnabled();
  const isAdmin = hasRole('admin');
  const userCompanyId = user?.company_id;
  const currentTenantId = tenant.tenant_id; // Use tenant from URL, not user profile
  const hasCompany = !!userCompanyId;

  const [filters, setFilters] = useState<FilterOptions>({
    timePeriod: 'all',
  });

  // Fetch leaderboard data (tenant-scoped for white-label admins, company-scoped for others)
  const {
    data: leaderboard = [],
    isLoading: isLoadingLeaderboard,
    refetch: refetchLeaderboard,
  } = useQuery({
    queryKey: ['leaderboard', filters, userCompanyId, isAdmin, currentTenantId, cpdEnabled],
    queryFn: () => fetchLeaderboard(filters, userCompanyId, isAdmin, currentTenantId, { includeCpd: cpdEnabled }),
    refetchInterval: 60000, // Auto-refresh every 60 seconds
    enabled: isAdmin || hasCompany, // Don't fetch if no company
  });

  // Fetch user rank (company-scoped for non-admins)
  const { data: userRank, isLoading: isLoadingUserRank } = useQuery({
    queryKey: ['userRank', user?.id, userCompanyId, isAdmin, cpdEnabled],
    queryFn: () => fetchUserRank(user?.id || '', userCompanyId, isAdmin, { includeCpd: cpdEnabled }),
    enabled: !!user?.id && (isAdmin || hasCompany),
  });

  // Fetch agents for filter
  const { data: agents = [] } = useQuery({
    queryKey: ['agents-filter'],
    queryFn: fetchAgentsForFilter,
  });

  // Fetch companies for filter
  const { data: companies = [] } = useQuery({
    queryKey: ['companies-filter'],
    queryFn: fetchCompaniesForFilter,
  });

  // Check if current user is in top 20
  const userInTop20 = leaderboard.some((entry) => entry.user_id === user?.id);
  const userEntry = leaderboard.find((entry) => entry.user_id === user?.id);

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Trophy className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">
              {isAdmin 
                ? 'Global Leaderboards' 
                : user?.company_name 
                  ? `${user.company_name} Leaderboard`
                  : 'Leaderboards'
              }
            </h1>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetchLeaderboard()}
            disabled={isLoadingLeaderboard}
          >
            <RefreshCw className={`h-4 w-4 ${isLoadingLeaderboard ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-muted-foreground">
          {isAdmin 
            ? 'See how practitioners rank across all companies' 
            : hasCompany 
              ? 'See how you rank against your team'
              : 'See your performance statistics'
          }
        </p>
      </div>

      {/* No Company State */}
      {!isAdmin && !hasCompany && (
        <div className="bg-muted/50 rounded-lg p-8 text-center mb-8">
          <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Join a Company to See Rankings</h3>
          <p className="text-muted-foreground mb-4">
            Team leaderboards are available once you're part of a company.
          </p>
        </div>
      )}

      {/* Filters - Only show if admin or has company */}
      {(isAdmin || hasCompany) && (
        <LeaderboardFilters
          filters={filters}
          onFiltersChange={setFilters}
          agents={agents}
          companies={companies}
          isAdmin={isAdmin}
        />
      )}

      {/* Leaderboard Table - Only show if admin or has company */}
      {(isAdmin || hasCompany) && (
        <>
          {isLoadingLeaderboard ? (
            <div className="space-y-2 mb-8">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="mb-8">
              <LeaderboardTable entries={leaderboard} currentUserId={user?.id} showCpd={cpdEnabled} />

              {/* Show current user at bottom if not in top 20 */}
              {!userInTop20 && userEntry && userRank && (
                <div className="mt-4">
                  <div className="text-center text-muted-foreground mb-2">...</div>
                  <LeaderboardTable
                    entries={[
                      {
                        ...userEntry,
                        rank: userRank.rank,
                      },
                    ]}
                    currentUserId={user?.id}
                    showCpd={cpdEnabled}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* User Stats Card */}
      <UserStatsCard 
        userRank={userRank || null} 
        isLoading={isLoadingUserRank}
        companyName={user?.company_name}
        isCompanyScoped={!isAdmin && hasCompany}
      />
    </div>
  );
}
